import { faPlay } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { ReactElement, useEffect, useRef, useState } from 'react';
import ReactPlayer from 'react-player/lazy';
import Image from './Image';

interface MediaProps {
  src: string | string[];
  loader?: ReactElement;
  isActive?: boolean;
  loop?: boolean;
  alt?: string;
  className?: string;
  showControls?: boolean;
  onEnded?: () => void;
}

const Media: React.FC<MediaProps> = ({
  src,
  loader = <div className="h-full w-full animate-shimmer rounded-xl" />,
  showControls = false,
  loop = true,
  onEnded,
  ...props
}) => {
  // We are using a separate property instead of isActive directly, to avoid race condition between useEffect and ReactPlayer
  // If ReactPlayer is rendered first, it's stopped before seekTo() and seek has no effect
  const [shouldPlay, setShouldPlay] = useState(false);
  const [showPlayButton, setShowPlayButton] = useState(showControls);
  const playerRef = useRef<ReactPlayer>(null);
  // For arrays of sources, check if any of them are videos
  const videos = Array.isArray(src)
    ? src.filter((s) => ReactPlayer.canPlay(s))
    : [];
  const images = Array.isArray(src)
    ? src.filter((s) => !ReactPlayer.canPlay(s))
    : [];

  useEffect(() => {
    playerRef.current?.seekTo(0);
    setShouldPlay(!!props.isActive && !showControls);
  }, [!!props.isActive, showControls]);

  const handlePlayClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowPlayButton(false);
    setShouldPlay(true);
  };

  if (videos.length > 0) {
    return (
      <div className="relative h-full w-full">
        <ReactPlayer
          ref={playerRef}
          url={videos}
          loop={loop}
          muted={!showControls}
          playing={shouldPlay}
          controls={showControls && !showPlayButton}
          onEnded={onEnded}
          {...props}
          fallback={
            images.length ? <MediaImage src={images} {...props} /> : loader
          }
        />
        {showPlayButton && (
          <div
            className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 cursor-pointer"
            onClick={handlePlayClick}
          >
            <div className="w-20 h-20 rounded-full bg-white flex items-center justify-center shadow-lg">
              <FontAwesomeIcon
                icon={faPlay}
                className="text-gray-600 text-3xl ml-1"
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  return <MediaImage src={images} loader={loader} {...props} />;
};

const MediaImage: React.FC<MediaProps> = ({ src, loader, ...props }) => {
  return <Image src={src} {...props} retries={60 * 60 * 24} loader={loader} />;
};

export default Media;
